// PortDetailsCard.tsx
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import ServiceStatusValue from "kontrolEdge/components/Shared/ServiceStatusValue";
import { useMetaEditor } from "kontrolEdge/components/Shared/NameTagEdit/useMetaEditor";
import { EditableTagList } from "kontrolEdge/components/Shared/NameTagEdit/EditableTagList";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { getTextColor } from "kontrolEdge/helpers/getTextColor";
import { DetailLink } from "kontrolEdge/components/Shared/DetailLink";
import type { ServiceStatusVariant } from "kontrolEdge/helpers/serviceStatusTag";
import type { CarrierAccess } from "kontrolEdge/helpers/carrierAccess";
import CarrierAccessSummary from "kontrolEdge/components/ExtenderPop/CarrierAccessSummary";
import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { usePhoneFold } from "kontrolEdge/components/Shared/DetailSummary/usePhoneFold";

/**
 * A row of a Port detail card, and the only declaration of it.
 *
 * It lives beside the component that renders it rather than in
 * `PortDetailsData.tsx` — that file is the connections table inside this
 * detail, a different shape — and rather than in `PortDetails.tsx`, which is a
 * page: other modules importing a type out of a page is how the second copy
 * got made in the first place.
 *
 * There were two until CTX-777, and they had drifted: this one carried `Icon`
 * and a `string[]` value, the other did not. `mapFetchedDataToPortDetails`
 * typed its output against that other one while this card rendered against
 * this one, so the mapper could emit a field the card never knew about, or the
 * card read one the mapper never promised, and TypeScript had nothing to say —
 * each side was consistent with its own copy. Adding `statusVariant` for
 * CTX-760 meant editing both, which is what surfaced it.
 */
export interface PortDetailItem {
  label: string;
  value?: string | number | boolean | React.ReactNode | string[];
  type?: string;
  variant?: string;
  Icon?: React.ComponentType<{ className?: string }>;
  tooltip?: string;
  /**
   * Severity of a status row, decided by `resolveServiceStatusTag` and carried
   * here so the card can paint a `GenericTag` with it. Present only on status
   * rows — its absence is what tells the card to render plain text.
   */
  statusVariant?: ServiceStatusVariant;
  /** Detail view the value links to, e.g. the port's Extended PoP (CTX-802). */
  to?: string;
  /**
   * Legs backing a sold Backhaul (84-): the row then shows its id, redundancy
   * and legs instead of the plain value.
   */
  backhaulLegs?: CarrierAccess[];
}

export interface PortDetailSection {
  header: string;
  isCollapsible?: boolean;
  initialExpanded?: boolean;
  items: PortDetailItem[];
  table?: never; // keep parity with others if you later add tables
}

type Props = {
  data: PortDetailSection;
  /** Optional extra content on the right side of the card header */
  rightContent?: React.ReactNode;
  /**
   * Pre-hydrated tag list from the detail GET inline `tags` field (§5.5).
   * Forwarded to `EditableTagList` so the picker mounts without an extra
   * §5.1 round-trip.
   */
  initialAssignedTags?: ResourceTagSummary[];
  /**
   * Tenant the resource actually lives in, sourced from the detail GET
   * (`raw.tenantId`). Forwarded to the Tags API so writes hit the right
   * scope regardless of the SideNav lens — the backend's resource-side
   * adapter is strict on `(id, tenantId)` and doesn't honor the
   * partner→child hierarchy. See `PortDetails.tsx` for the rationale.
   */
  resourceTenantId?: number;
};

const PortDetailsCard: React.FC<Props> = ({
  data,
  rightContent,
  initialAssignedTags,
  resourceTenantId,
}) => {
  const api = useAxiosAuth0();
  const [searchParams] = useSearchParams();
  const portId = searchParams.get("portId") ?? null;
  const shouldCollapse = data.isCollapsible ?? true;
  // Folded on a phone, with a one-line summary (CTX-1421).
  const fold = usePhoneFold(data, { isCollapsible: shouldCollapse, initialExpanded: data.initialExpanded });
  const { tenantId } = useAppSelector((state) => state.selection);

  // Seed values (like other services)
  const nameSeed = String(
    data.items.find((i) => i.label === "Name")?.value ?? ""
  );
  const tagSeed = (() => {
    const v = data.items.find((i) => i.label === "Tag")?.value;
    if (Array.isArray(v)) {
      return v.join(", ");
    }
    if (typeof v === "string" && v !== "-") {
      // If it's a comma-separated string, return as is
      return v;
    }
    return "";
  })();

  const meta = useMetaEditor({
    api,
    kind: "port",
    resourceId: portId,
    tenantId,
    initialName: nameSeed,
    initialTag: tagSeed,
  });

  // Convert tag string to array for EditableTagList
  const tagArray = useMemo(() => {
    if (!meta.tagValue || meta.tagValue.trim() === "") return [];
    return meta.tagValue.split(",").map(t => t.trim()).filter(t => t !== "");
  }, [meta.tagValue]);

  return (
    <GenericCard
      {...fold}
      rightContent={rightContent}
      bodyContent={
        <div className="space-y-2">
          {data.items.map((item, idx) => {
            // Editable: Name
            if (item.label === "Name") {
              return (
                <div
                  key={idx}
                  className="flex justify-between w-full py-1 border-b"
                >
                  <h3 className="text-sm text-semantic-text-primary">Name</h3>
                  <div className="flex items-center gap-2">
                    <GenericTag
                      label={meta.nameValue || "—"}
                      variant="neutral"
                      size="small"
                      minWidth="100px"
                      className="text-center"
                      editable={true}
                      value={meta.nameValue}
                      onSave={(newValue) => {
                        meta.saveName(newValue);
                        meta.setNameValue(newValue);
                      }}
                      onCancel={() => {
                        meta.cancelName();
                      }}
                      isSaving={meta.savingField === "name"}
                      disabled={meta.savingField !== null}
                    />
                  </div>
                </div>
              );
            }

            // Editable: Tag (using EditableTagList for array support)
            if (item.label === "Tag") {
              return (
                <div
                  key={idx}
                  className="flex justify-between w-full py-1 border-b"
                >
                  <h3 className="text-sm text-semantic-text-primary">Tag</h3>
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                      <EditableTagList
                      tags={tagArray}
                      onUpdate={meta.updateTagArray}
                      disabled={meta.savingField !== null}
                      isLoading={meta.isSavingTag}
                      resourceType="ke-port"
                      resourceId={portId}
                      initialAssignedTags={initialAssignedTags}
                      tenantId={resourceTenantId}
                    />
                  </div>
                </div>
              );
            }

            // A group heading ("Subscription Details"), as the other detail
            // cards draw it; as a plain row it read as a field set to "-".
            if (item.type === "subtitle") {
              return (
                <div key={idx} className="pt-3 pb-1">
                  <h4 className="text-sm font-medium text-semantic-text-secondary">
                    {item.label}
                  </h4>
                </div>
              );
            }

            // A message row carries no value, so the label/value layout below
            // rendered its empty value column as a dash — "…data is
            // unavailable at the moment   -", where the dash reads as a field
            // nobody filled in rather than as part of the sentence.
            if (item.type === "error") {
              return (
                <div
                  key={idx}
                  className={`w-full py-1 ${
                    idx !== data.items.length - 1 ? "border-b" : ""
                  }`}
                >
                  <p className="text-sm text-semantic-text-secondary">
                    {item.label}
                  </p>
                </div>
              );
            }

            // Default read-only row
            return (
              <div
                key={idx}
                className={`${item.statusVariant ? "hidden sm:flex" : "flex"} justify-between w-full py-1 ${
                  idx !== data.items.length - 1 ? "border-b" : ""
                }`}
              >
                <h3 className="text-sm text-semantic-text-primary">
                  {item.label}
                </h3>
                <div className="flex items-center gap-2">
                  {item.Icon && <item.Icon className="w-6 h-6 text-gray-500" />}
                  {item.statusVariant ? (
                    <ServiceStatusValue
                      label={String(item.value ?? "-")}
                      variant={item.statusVariant}
                      tooltip={item.tooltip}
                    />
                  ) : item.backhaulLegs ? (
                    <CarrierAccessSummary carriers={item.backhaulLegs} align="end" partial />
                  ) : item.type === "tag" ? (
                    <GenericTag label={String(item.value)} variant="success" />
                  ) : item.to && item.value ? (
                    <DetailLink to={item.to} label={String(item.value)} />
                  ) : item.value ? (
                    item.tooltip ? (
                      <GenericTooltip
                        text={item.tooltip}
                        position="bottom"
                        horizontalAlign="right"
                        width="20rem"
                      >
                        <span
                          className={`text-sm ${getTextColor(
                            item.label,
                            String(item.value)
                          )}`}
                        >
                          {String(item.value)}
                        </span>
                      </GenericTooltip>
                    ) : (
                      <p
                        className={`text-sm ${getTextColor(
                          item.label,
                          String(item.value)
                        )}`}
                      >
                        {String(item.value)}
                      </p>
                    )
                  ) : (
                    <p className="text-sm text-gray-500">-</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      }
    />
  );
};

export default PortDetailsCard;
